import React, { useEffect, useRef } from 'react';
import { FC } from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  BarController,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Box, Typography } from '@mui/material';

interface Props {
  chartType?: string;
  data: any;
  width?: string;
}

const BarChart: FC<Props> = ({ data, chartType, width }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);

  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    BarController,
    ChartDataLabels
  );

  const splitClosestSpaceAtPosition = (
    inputString: string,
    position: number
  ) => {
    if (position >= inputString.length) {
      return [inputString];
    }

    var closestSpaceIndex = position;
    for (var i = position; i >= 0; i--) {
      if (inputString[i] === ' ') {
        closestSpaceIndex = i;
        break;
      }
    }

    var firstPart = inputString.substring(0, closestSpaceIndex);
    var secondPart = inputString.substring(closestSpaceIndex + 1);
    return [firstPart, secondPart];
  };

  const formatChartLabel = (label: string) => {
    if (label.length > 80) {
      const strArr = splitClosestSpaceAtPosition(label, 80);
      return strArr;
    }
    return label;
  };

  useEffect(() => {
    if (chartRef.current && data) {
      const ctx = chartRef.current.getContext('2d');

      if (ctx) {
        const stackedBarArray = data.optionLabels.map((_item: number) => 100);
        const formattedLabels = data.optionLabels.map((item: string) =>
          formatChartLabel(item)
        );
        const chartData: any = {
          labels: formattedLabels,
          borderWidth: 0,
          datasets: [
            {
              backgroundColor: (bgColor: { index: number }) => {
                const selectedIndex = data.optionLabels.indexOf(
                  data.optionText
                );
                return bgColor.index === selectedIndex
                  ? 'rgba(106, 171, 222, 1)'
                  : 'rgba(180, 180, 180, 1)';
              },
              data: data.data,
              fontSize: '16px',
            },
            {
              backgroundColor: 'rgba(237, 237, 237, 1)',
              data: stackedBarArray,
            },
          ],
        };

        const options: any = {
          indexAxis: chartType === 'horizontal' ? 'y' : 'x',
          scales: {
            y: {
              stacked: true,
              beginAtZero: true,
              grid: {
                display: false,
                drawBorder: false,
              },
              max: 100,
              ticks: {
                display: chartType === 'horizontal' ? true : false,
              },
            },
            x: {
              ticks: {
                display: chartType === 'horizontal' ? false : true,
                fontSize: 20,
              },
              stacked: true,
              grid: {
                display: false,
                drawBorder: false,
              },
              max: 100,
            },
          },
          plugins: {
            tooltip: {
              enabled: false,
            },
            legend: {
              display: false,
            },
            datalabels: {
              anchor: 'end',
              align: chartType === 'horizontal' ? 'end' : 'top',
              formatter: (value: number) => `${value}%`,
              color: 'black',
              font: {
                weight: 'bold',
                size: 18,
              },
            },
          },
        };

        // Create the bar chart
        const myChart = new ChartJS(chartRef.current, {
          type: 'bar',
          data: chartData,
          options: options,
        });

        // We need to destroy the reference to plot multiple graphs on the same page
        return () => {
          myChart.destroy();
        };
      }
    }
  }, [chartRef, data]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        marginTop: '50px',
        pb: 4,
        borderBottom: '1px solid #000',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Open Sans',
          fontSize: '18px',
          mt: 2,
          textAlign: 'center',
        }}
      >
        {data.title}
      </Typography>
      {data.description && (
        <Typography
          sx={{
            fontFamily: 'Open Sans',
            fontSize: '18px',
            mt: 2,
            textAlign: 'center',
          }}
        >
          {data.description}
        </Typography>
      )}
      <Typography
        sx={{
          fontFamily: 'Open Sans',
          fontSize: '16px',
          fontWeight: '800',
          mt: 1,
          textAlign: 'center',
        }}
      >
        Your Answer: {data.optionText}
      </Typography>
      <div style={{ width: width ? width : '900px', marginTop: '35px' }}>
        {data.id && <canvas ref={chartRef} id={data.id}></canvas>}
      </div>
    </Box>
  );
};

export default BarChart;
